/**
 * Sync Manager
 * 
 * Handles synchronization of offline actions when connectivity is restored.
 * Uses mutex locking to prevent concurrent syncs and includes iOS fallback
 * via online/visibilitychange events.
 */
import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';
import {
  getPendingActions,
  getAllActions,
  removeAction,
  markActionProcessing,
  incrementRetry,
  markActionFailed,
  updateAction,
  PendingAction,
  MAX_RETRIES,
} from './action-queue';
import { checkForConflict, ConflictInfo } from './conflict-resolver';

export interface SyncResult {
  succeeded: number;
  failed: number;
  conflicts: number;
  duration: number;
}

export type SyncEvent = 
  | { type: 'sync_start' }
  | { type: 'sync_complete'; result: SyncResult }
  | { type: 'action_synced'; actionId: string }
  | { type: 'action_failed'; actionId: string; error: string }
  | { type: 'conflict_detected'; conflict: ConflictInfo }
  | { type: 'pending_count_changed'; count: number };

type SyncListener = (event: SyncEvent) => void;

const BASE_DELAY = 1000; // 1 second base delay for exponential backoff

class SyncManager {
  private syncPromise: Promise<SyncResult> | null = null;
  private listeners: Set<SyncListener> = new Set();
  private initialized = false;

  /**
   * Initialize sync manager - sets up event listeners
   * Call this once on app startup
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Reset stale processing actions (from interrupted syncs)
    this.resetStaleActions();

    // iOS fallback: online event
    window.addEventListener('online', () => {
      console.info('[SyncManager] Online event detected');
      this.sync();
    });

    // iOS fallback: visibility change (app comes to foreground)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        console.info('[SyncManager] Visibility change detected, syncing...');
        this.sync();
      }
    });

    console.info('[SyncManager] Initialized');
  }

  /**
   * Reset actions stuck in 'processing' state (e.g., from app crash during sync)
   */
  private async resetStaleActions(): Promise<void> {
    try {
      const actions = await getAllActions();
      const staleThreshold = 60000; // 1 minute
      
      for (const action of actions) {
        if (action.actionStatus === 'processing' && 
            Date.now() - action.timestamp > staleThreshold) {
          action.actionStatus = 'pending';
          await updateAction(action);
          console.info(`[SyncManager] Reset stale action: ${action.id}`);
        }
      }
    } catch (error) {
      console.error('[SyncManager] Failed to reset stale actions:', error);
    }
  }

  /**
   * Subscribe to sync events
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Emit an event to all listeners
   */
  private emit(event: SyncEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('[SyncManager] Listener error:', error);
      }
    });
  }

  /**
   * Mutex-locked sync - prevents concurrent sync operations
   */
  async sync(): Promise<SyncResult> {
    // If already syncing, return the existing promise
    if (this.syncPromise) {
      console.info('[SyncManager] Sync already in progress, waiting...');
      return this.syncPromise;
    }

    // Not online? Skip sync
    if (!navigator.onLine) {
      return { succeeded: 0, failed: 0, conflicts: 0, duration: 0 };
    }

    this.syncPromise = this.doSync();
    try {
      return await this.syncPromise;
    } finally {
      this.syncPromise = null;
    }
  }

  /**
   * Internal sync implementation
   */
  private async doSync(): Promise<SyncResult> {
    const startTime = Date.now();
    this.emit({ type: 'sync_start' });

    let succeeded = 0;
    let failed = 0;
    let conflicts = 0;

    try {
      const actions = await getPendingActions();
      
      if (actions.length === 0) {
        console.info('[SyncManager] No pending actions to sync');
        return { succeeded: 0, failed: 0, conflicts: 0, duration: Date.now() - startTime };
      }

      console.info(`[SyncManager] Starting sync of ${actions.length} actions`);

      for (const action of actions) {
        let wasMarkedProcessing = false;
        
        try {
          // Mark as processing
          await markActionProcessing(action.id);
          wasMarkedProcessing = true;

          // Check for conflicts
          const conflictInfo = await checkForConflict(action);
          if (conflictInfo) {
            conflicts++;
            this.emit({ type: 'conflict_detected', conflict: conflictInfo });
            // Reset status back to pending for conflict resolution
            await updateAction({ ...action, actionStatus: 'pending' });
            continue;
          }

          // Execute the action
          await this.executeAction(action);
          
          // Success - remove from queue
          await removeAction(action.id);
          succeeded++;
          this.emit({ type: 'action_synced', actionId: action.id });

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Tuntematon virhe';
          
          if (import.meta.env.DEV) {
            console.error(`[SyncManager] Action ${action.id} failed:`, error);
          }

          // Report sync failures to Sentry - critical for data loss prevention
          Sentry.captureException(error, {
            contexts: {
              sync_action: {
                actionId: action.id,
                type: action.type,
                retries: action.retries,
              },
            },
          });

          // Retry with exponential backoff
          const canRetry = await incrementRetry(action.id, errorMessage);
          
          if (!canRetry) {
            failed++;
            this.emit({ type: 'action_failed', actionId: action.id, error: errorMessage });
          } else {
            // Wait before next retry (exponential backoff)
            const delay = BASE_DELAY * Math.pow(2, action.retries);
            if (import.meta.env.DEV) {
              console.info(`[SyncManager] Will retry ${action.id} in ${delay}ms`);
            }
            await this.sleep(delay);
          }
        } finally {
          // Varmista ettei jää processing-tilaan virhetilanteessa
          if (wasMarkedProcessing) {
            try {
              const currentAction = (await getAllActions()).find(a => a.id === action.id);
              if (currentAction?.actionStatus === 'processing') {
                await updateAction({ ...currentAction, actionStatus: 'pending' });
                if (import.meta.env.DEV) {
                  console.info(`[SyncManager] Reset stuck processing action: ${action.id}`);
                }
              }
            } catch {
              // Ignore cleanup errors
            }
          }
        }
      }

    } catch (error) {
      console.error('[SyncManager] Sync error:', error);
    }

    const duration = Date.now() - startTime;
    const result: SyncResult = { succeeded, failed, conflicts, duration };

    // Telemetry
    console.info(
      `[SyncManager] Sync complete: ${succeeded}/${succeeded + failed + conflicts} succeeded, ` +
      `${conflicts} conflicts, ${failed} failed, duration ${duration}ms`
    );

    this.emit({ type: 'sync_complete', result });
    
    // Emit updated pending count
    const pendingActions = await getPendingActions();
    this.emit({ type: 'pending_count_changed', count: pendingActions.length });

    return result;
  }

  /**
   * Execute a single action against Supabase
   */
  private async executeAction(action: PendingAction): Promise<void> {
    switch (action.type) {
      case 'UPDATE_INSTALLATION_STATUS':
        await this.executeInstallationUpdate(action);
        break;
      case 'UPDATE_LOAD_STATUS':
        await this.executeLoadUpdate(action);
        break;
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }

  /**
   * Execute installation status update
   */
  private async executeInstallationUpdate(action: PendingAction): Promise<void> {
    const { installationId, status, notes } = action.payload;
    
    if (!installationId || !status) {
      throw new Error('Missing installationId or status');
    }

    const updates: Record<string, unknown> = { status };
    
    if (status === 'completed') {
      updates.completed_date = new Date().toISOString().split('T')[0];
    }
    if (notes) {
      updates.notes = notes;
    }

    const { error } = await supabase
      .from('installations')
      .update(updates)
      .eq('id', installationId);

    if (error) throw error;
  }

  /**
   * Execute load status update
   */
  private async executeLoadUpdate(action: PendingAction): Promise<void> {
    const { loadId, status } = action.payload;
    
    if (!loadId || !status) {
      throw new Error('Missing loadId or status');
    }

    const { error } = await supabase
      .from('installation_loads')
      .update({ status })
      .eq('id', loadId);

    if (error) throw error;
  }

  /**
   * Helper to sleep for a given duration
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Force sync now (user-triggered)
   */
  async forceSync(): Promise<SyncResult> {
    return this.sync();
  }
}

// Singleton instance
export const syncManager = new SyncManager();
